import { Check, CloseButton, ErrorIcon } from "components/atoms/Icon";
import { Header, Paragraph } from "components/atoms/Typography";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useUploadState from "recoilStore/useUploadState";
import styles from "./UploadBanner.module.scss";

function UploadBanner() {
    const { state } = useUploadState();

    if (!state.showBanner || state.files.length === 0) return null;

    return createPortal(<Banner />, document.getElementById("root")!);
}

function Banner() {
    const { state, resetStateToDefault, isAllCompleted, hasAnyFailed, getOverallProgress } = useUploadState();
    const autoCloseTimeout = useRef<NodeJS.Timeout | null>(null);

    const allCompleted = isAllCompleted();
    const anyFailed = hasAnyFailed();
    const progress = getOverallProgress();

    const handleClose = () => {
        resetStateToDefault();
    };

    useEffect(() => {
        if (allCompleted && !anyFailed) {
            autoCloseTimeout.current = setTimeout(() => {
                resetStateToDefault();
            }, 5000);
        }

        return () => {
            if (autoCloseTimeout.current) {
                clearTimeout(autoCloseTimeout.current);
            }
        };
    }, [allCompleted, anyFailed]);

    const uploadingCount = state.files.filter((f) => f.status === "uploading" || f.status === "pending").length;
    const completedCount = state.files.filter((f) => f.status === "completed").length;
    const failedCount = state.files.filter((f) => f.status === "failed").length;

    if (allCompleted && !anyFailed) {
        return (
            <div className={styles.uploadBanner}>
                <div className={styles.header}>
                    <Check />
                    <Header size="x-small" style={{ color: "var(--audette-content-positive)" }}>
                        Upload complete
                    </Header>
                    <CloseButton onClick={handleClose} />
                </div>
                <Paragraph size="small" style={{ color: "var(--audette-gray-500)" }}>
                    {completedCount} file{completedCount !== 1 ? "s" : ""} uploaded successfully
                </Paragraph>
            </div>
        );
    }

    if (anyFailed && uploadingCount === 0) {
        return (
            <div className={styles.uploadBanner}>
                <div className={styles.header}>
                    <ErrorIcon />
                    <Header size="x-small">Upload failed</Header>
                    <CloseButton onClick={handleClose} />
                </div>
                <Paragraph size="small" style={{ color: "var(--audette-gray-500)" }}>
                    {failedCount} file{failedCount !== 1 ? "s" : ""} failed to upload
                </Paragraph>
            </div>
        );
    }

    return (
        <div className={styles.uploadBanner}>
            <div className={styles.header}>
                <Header size="x-small">
                    Uploading {uploadingCount} file
                    {uploadingCount !== 1 ? "s" : ""}...
                </Header>
                <CloseButton onClick={handleClose} />
            </div>

            <div className={styles.files}>
                {state.files.map((file) => (
                    <FileProgress key={file.fileName} file={file} />
                ))}
            </div>

            <div className={styles.overall}>
                <Paragraph size="small" style={{ color: "var(--audette-gray-500)" }}>
                    Overall progress: {progress}%
                </Paragraph>
            </div>

            <div className={styles.loadingBarContainer}>
                <div style={{ width: `${progress}%` }} className={styles.loadingBar} />
            </div>
        </div>
    );
}

interface FileProgressProps {
    file: {
        fileName: string;
        uploadedChunks: number;
        totalChunks: number;
        status: "pending" | "uploading" | "completed" | "failed";
        errorMessage?: string;
    };
}

function FileProgress({ file }: FileProgressProps) {
    const progress = file.totalChunks > 0 ? Math.round((file.uploadedChunks / file.totalChunks) * 100) : 0;

    const getStatusIcon = () => {
        switch (file.status) {
            case "completed":
                return <Check />;
            case "failed":
                return <ErrorIcon />;
            default:
                return null;
        }
    };

    const getStatusColor = () => {
        switch (file.status) {
            case "completed":
                return "var(--audette-content-positive)";
            case "failed":
                return "var(--audette-content-negative)";
            default:
                return "var(--audette-gray-500)";
        }
    };

    return (
        <div className={styles.file}>
            <div className={styles.fileInfo}>
                {getStatusIcon()}
                <Paragraph
                    size="small"
                    style={{
                        color: getStatusColor(),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "180px",
                    }}
                >
                    {file.fileName}
                </Paragraph>
                <Paragraph
                    size="small"
                    style={{
                        color: "var(--audette-gray-400)",
                        marginLeft: "auto",
                    }}
                >
                    {file.status === "uploading" || file.status === "pending" ? `${progress}%` : ""}
                </Paragraph>
            </div>
            {(file.status === "uploading" || file.status === "pending") && (
                <div className={styles.fileLoadingBarContainer}>
                    <div style={{ width: `${progress}%` }} className={styles.fileLoadingBar} />
                </div>
            )}
        </div>
    );
}

export default UploadBanner;
